"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = getHandleAuthorization;

var _lodash = _interopRequireDefault(require("lodash.get"));

var _axios = _interopRequireDefault(require("axios"));

var _cookie = require("@rentspree/cookie");

var _debug = _interopRequireDefault(require("./debug"));

var _const = require("./const");

var _axiosConfig = require("./axios-config");

var _axiosRefreshToken = _interopRequireDefault(require("./axios-refresh-token"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/** @module Main */
var debug = (0, _debug.default)("rentspree-axios-refresh-token:main");
/**
 * @callback rejectionHandler
 * @param {AxiosError} e the axios error
 * @returns {Promise.reject} The rejection promise returned to axios error interceptors
 */

var defaultReturn = function defaultReturn(e) {
  return Promise.reject(e);
};
/**
 * This method generate the handle error function for Axios Error interceptors
 * It will generate the handleAuthorization function according to the given parameters.
 * The axiosInstance must be supplied if the instance had been config in a unique way,
 * This instance will be used to fire the retry request after retrieving the new access token.
 * @param {Object} config the config parameters
 * @param {rejectionHandler} config.afterRetry If the retry still got error, this function will be called. The function should return `Promise.reject` for Axios to work correctly.
 * @param {rejectionHandler | undefined} config.afterRefreshTokenFailure If the refresh token API return error, the handler will remove cookies and fire this function. The function also should return `Promise.reject`. If the function is not supplied, the handler will remove UserToken then throw error
 * @param {String} config.domain the domain of this website, this will be used for cookies
 * @param {String} config.authAPIBase the base API for auth service, of which the refresh token will be fired to.
 * @param {Function} config.getErrorResponse the function for extracting error in case the API does not return 401.
 * @param {AxiosInstance} config.axiosInstance the axios instance for firing the retry request after retriveing a new access token.
 * @returns {Promise<Object>} axios promise
 * @memberof Main
 * @example
 * // with minimum config
 * const apiInstance = axios.create({baseURL: "api.hello.com"})
 * const handleAuthorization = getHandleAuthorization({
 *   domain: "www.hello.com",
 *   authAPIBase: "auth.hello.com",
 *   axiosInstance: apiInstance
 * })
 * apiInstance.interceptors.response.use(r => r.data, handleAuthorization)
 * apiInstance.get(/**...*\/)
 * @example
 * // with some more config
 * const apiInstance = axios.create({baseURL: "api.hello.com"})
 * const handleAuthorization = getHandleAuthorization({
 *   afterRetry: (error) => {
 *     console.log(error)
 *     // do something else here
 *     location.redirect("/") // for example you can redirect the page here
 *     return Promise.reject(new Error("error here")) // you can reject your custom error
 *   }
 *   afterRefreshTokenFailure: (error) => Promise.reject(error.mess
 *   domain: "www.hello.com",
 *   authAPIBase: "auth.hello.com",
 *   axiosInstance: apiInstance
 * })
 * apiInstance.interceptors.response.use(r => r.data, handleAuthorization)
 * apiInstance.get(/**...*\/)
 */


function getHandleAuthorization(_ref) {
  var _ref$afterRetry = _ref.afterRetry,
      afterRetry = _ref$afterRetry === void 0 ? defaultReturn : _ref$afterRetry,
      afterRefreshTokenFailure = _ref.afterRefreshTokenFailure,
      domain = _ref.domain,
      authAPIBase = _ref.authAPIBase,
      _ref$getErrorResponse = _ref.getErrorResponse,
      getErrorResponse = _ref$getErrorResponse === void 0 ? function (e) {
    return (0, _lodash.default)(e, "response", e);
  } : _ref$getErrorResponse,
      _ref$axiosInstance = _ref.axiosInstance,
      axiosInstance = _ref$axiosInstance === void 0 ? _axios.default : _ref$axiosInstance;
  var cookieOption = (0, _const.getCookieOption)(domain);
  return function handleAuthorization(error) {
    var statusCode = (0, _lodash.default)(error, "response.status");
    var originalRequest = (0, _lodash.default)(error, "config", {});
    debug("handleAuthorization with status:%s and config: %o", statusCode, originalRequest);

    if (statusCode === 401) {
      // if this error request is already called, that means status 401 was not from
      // the token expired issue.
      if (originalRequest.retry) {
        // retried the API but still got error
        debug("go to retry");
        return afterRetry(error);
      }

      originalRequest.retry = true;
      return (0, _axiosRefreshToken.default)(authAPIBase).then(function (newToken) {
        // if request the new token is success, save it and retry the failed request
        debug("received new token: %s", newToken);
        (0, _cookie.setOption)(cookieOption);
        (0, _cookie.setLocalItem)(_const.STORAGE.USER_TOKEN, newToken);
        var aconfig = (0, _axiosConfig.axiosConfig)(newToken.access_token, originalRequest);
        debug("retry request with new config: %o", aconfig);
        return axiosInstance.request(aconfig);
      }).catch(function (err) {
        // get error on retrieve Refresh token
        // if request token is error, it should redirect the user to login page
        debug("exchangeRefreshToken throws with %o", err);

        if (!(0, _lodash.default)(err, "config.retry", false)) {
          // inside this is the case where the catch is from exchangeRefreshToken
          if (typeof afterRefreshTokenFailure === "function") {
            debug("return to afterRefreshTokenFailure");
            return afterRefreshTokenFailure(err);
          } // this is the default behaviour for the case of cannot get new accessToken


          debug("removeAllLocalItem");
          (0, _cookie.removeAllLocalItem)("rentspree", cookieOption);
        }

        debug("return Promise.reject");
        return Promise.reject(getErrorResponse(err));
      });
    }

    return Promise.reject(getErrorResponse(error));
  };
}